import React, { FC, useEffect, useState } from 'react'
import MainBreadcrumb from '../../../../component-controller-mod/component-serv/navigation-fs-cp/breadcrumb-nav-op/page-main-bcb/ts/main-bcb'
import { useNavigate, useParams } from 'react-router-dom'
import { EditIcon, HomeIcon, MiniStonkIcon } from '../../../../asset-controller-mod/asset-serv/icons/iconExporter'
import "../css/preview-header.css"
import SelectFieldNoLabel from '../../../../component-controller-mod/component-serv/input-fs-cp/select-field-op/ts/selectFieldNoLabel'
import { getDetailUuid, STATUS_OPTIONS } from './workflow.type'
import DateInput from '@core/uilibrary/dist/components/Calendar/ConceptualDropdownCalendar/RoundCubeDropdownCalendar/Calendar/DateInput/DateInput'
import { useCookies } from '../../../../context-controller-mod/context-serv/global-provider/cookies-context/cookiesContext'
import { useSafeNavigate } from '../../../../route-controller-mod/route-serv/route-controller/useSafeNavigate'
import { groupPermissionAccessWorkflow } from '../../../../context-controller-mod/context-serv/permission-provider/permissionExporter'

interface PreviewHeaderProp {
    pageName?: string;
    contentType?: string;
    createAt?: string;
    author?: string;
    pageStatus?: string;
    selectedStatus?: string;
    selectedDate?: Date | null;
    onStatusChange?: (value: string) => void;
    onDateChange?: (value: Date | null) => void;
    sectionId?: string;
}

const CONTENT_SEGMENTS = [
    "navigation",
    "product",
    "career",
    "contact",
    "sale",
    "business",
    "job-detail",
    "company",
];

const PreviewHeader: FC<PreviewHeaderProp> = ({
    pageName,
    contentType,
    createAt,
    author,
    pageStatus,
    selectedStatus,
    selectedDate,
    onStatusChange,
    onDateChange,
    sectionId,
}) => {
    const navigate = useSafeNavigate();
    const { currentId, versionId, mode } = useParams<{
        currentId: string;
        versionId: string;
        mode: string;
    }>();

    const [statusValue, setStatusValue] = useState<string>("");
    const isDraftMode = String(mode ?? "").toLowerCase() === "draft";
    const isScheduleSelected = statusValue === "scheduled";
    const [authorName, setAuthorName] = useState<string>("")
    const { deleteDetection } = useCookies();
    const canUpdate = groupPermissionAccessWorkflow.update();

    useEffect(() => {
        const fetchUser = async () => {
            if (!author) return;

            try {
                const res = await getDetailUuid(author);
                setAuthorName(res.data?.data?.username);
            } catch (error) {
                console.error(error);
            }
        };

        fetchUser();
    }, [author]);

    useEffect(() => {
        const nextStatus = String(selectedStatus ?? pageStatus ?? "").toLowerCase();
        setStatusValue(nextStatus);
    }, [selectedStatus, pageStatus]);

    const formatDate = (dateStr?: string) => {
        if (!dateStr) return "";
        const d = new Date(dateStr);
        return d.toISOString().split("T")[0];
    };
    const toKebabCase = (value: string) =>
        value.replace(/([a-z])([A-Z])/g, "$1-$2").toLowerCase(); //NOSONAR

    const resolveContentPath = (pageName?: string) => {
        if (!pageName) return null;

        const normalized = pageName
            .toLowerCase()
            .replace(/page$/, "")
            .replace(/\s+/g, "-");

        const segment = CONTENT_SEGMENTS.find((s) =>
            normalized.startsWith(s)
        );

        return segment ? `/content/${segment}` : null;
    };

    const handleEditNavigate = () => {
        if (!currentId || !versionId || !sectionId) {
            console.warn("Missing contentUuid, contentVersionUuid, or sectionId");
            return;
        }

        const normalizedPage = String(pageName ?? "").toLowerCase();

        // SPECIAL CASE → navigation pages
        if (normalizedPage === "header" || normalizedPage === "footer") {
            navigate(
                `/content/navigation/${currentId}/${versionId}/${normalizedPage}/edit`,
                undefined,
                () => {
                    deleteDetection();
                }
            );
            return;
        }

        const resolvedPath = resolveContentPath(pageName);

        if (resolvedPath) {
            navigate(
                `${resolvedPath}/${currentId}/${versionId}/${sectionId}/edit`,
                undefined,
                () => {
                    deleteDetection();
                }
            );
            return;
        }

        // fallback
        const kebabPage = toKebabCase(pageName ?? "");
        navigate(
            `/content/${kebabPage}/${currentId}/${versionId}/${sectionId}/edit`,
            undefined,
            () => {
                deleteDetection();
            }
        );
    };

    return (
        <main className='scl--prev-head-main-container'>
            <section className="scl--prev-head-const-bread-sec-on">
                <button onClick={() => navigate("/career/hire")}>
                    <HomeIcon />
                    <span>Home</span>
                </button>
                <span>/</span>
                <h6>
                    <MiniStonkIcon />
                    <span className="span-bread">Workflow</span>
                </h6>
            </section>

            <section className='scl--prev-head-btn-update-sty'>
                <MainBreadcrumb
                    title="Preview content"
                    paragraph="Manage content approval workflow and track submission status."
                />
                {isDraftMode && (
                    <button
                        className='btn-section-two'
                        onClick={handleEditNavigate}
                    >
                        <EditIcon />
                    </button>
                )}
            </section>

            <section className='scl--detail-info-preview-cnst-sty'>
                <div className='scl--item-prev-const-sty'>
                    <div className='scl--singl-prev-const-sty first-item'>
                        <span className='span-class'>Content Page:</span>
                        <h6>{pageName}</h6>
                    </div>

                    <div className='scl--singl-prev-const-sty'>
                        <span className='span-class'>Content type:</span>
                        <h6>{contentType}</h6>
                    </div>
                </div>

                <div className='scl--item-prev-const-sty'>
                    <div className='scl--singl-prev-const-sty first-item'>
                        <span className='span-class'>Create at:</span>
                        <h6>{formatDate(createAt)}</h6>
                    </div>

                    <div className='scl--singl-prev-const-sty'>
                        <span className='span-class'>Author:</span>
                        <h6>{authorName}</h6>
                    </div>
                </div>

                {canUpdate &&
                    <div className='scl--item-prev-const-sty'>
                        <div className='scl--singl-prev-const-sty first-item'>
                            <span className='span-class'>Status:</span>
                            <div className='status-item'>
                                <SelectFieldNoLabel
                                    name="status"
                                    placeholder="Status"
                                    value={statusValue}
                                    options={STATUS_OPTIONS}
                                    onChange={(value) => {
                                        const nextValue = String(value).toLowerCase();
                                        setStatusValue(nextValue);
                                        onStatusChange?.(nextValue);
                                    }}
                                />
                            </div>
                        </div>

                        <div className='scl--singl-prev-const-sty'>
                            <span className='span-class'>Schedule date:</span>
                            <DateInput
                                id="startDate"
                                name="startDate"
                                value={selectedDate ?? null}
                                onChange={(value) => onDateChange?.(value)}
                                placeholder="DD/MM/YYYY"
                                disabled={!isScheduleSelected}
                            />
                        </div>
                    </div>
                }

            </section>
        </main>
    );
};

export default PreviewHeader;